import { useState, useEffect } from 'react';
import { resolvePaymentSession } from '../../../dashboard/services/reportingApi';
import { getSettlementUsd } from '../../utils/transactionUtils';

function getConfirmMessage(action, txn) {
  switch (action) {
    case 'accept_partial':
      return `Accept the partial payment of $${getSettlementUsd(txn) ?? txn.order_amount_usd} and process this order?`;
    case 'deny':
      return 'Deny this payment? You will need to refund the customer manually via store credit, check, or other method.';
    case 'keep_full':
      return `Keep the full overpayment of $${getSettlementUsd(txn) ?? txn.order_amount_usd}? Processing fees will apply to the full amount.`;
    case 'partial_refund':
      return `Refund the overage and keep $${txn.expected_payment_usd || txn.order_amount_usd}? You will need to refund the difference manually.`;
    default:
      return 'Are you sure you want to proceed?';
  }
}

/**
 * Hook to manage resolution state for discrepancy handling.
 * Handles accept_partial, deny, keep_full, and partial_refund actions.
 */
export default function useResolution(transaction, token, onResolve, onClose) {
  const [resolving, setResolving] = useState(false);
  const [resolutionError, setResolutionError] = useState(null);
  const [resolutionSuccess, setResolutionSuccess] = useState(false);

  // Reset when transaction changes
  useEffect(() => {
    setResolutionSuccess(false);
    setResolutionError(null);
    setResolving(false);
  }, [transaction?.session_id]);

  const handleResolve = async (action, acceptedAmount = null, refundAmount = null) => {
    if (!confirm(getConfirmMessage(action, transaction))) {
      return;
    }

    setResolving(true);
    setResolutionError(null);

    try {
      const payload = { action };
      if (acceptedAmount !== null) {
        payload.accepted_amount_usd = Math.round(acceptedAmount * 100) / 100;
      }
      if (refundAmount !== null) {
        payload.refund_amount_usd = Math.round(refundAmount * 100) / 100;
      }

      await resolvePaymentSession(transaction.session_id, payload, token);

      setResolutionSuccess(true);

      if (onResolve) {
        setTimeout(() => {
          onResolve();
          onClose();
        }, 1500);
      }
    } catch (err) {
      setResolutionError(err.message || 'Resolution failed');
    } finally {
      setResolving(false);
    }
  };

  // setResolutionSuccess is exposed so flows that complete a resolution outside
  // handleResolve (e.g. the inline mark-refunded button) can show the same
  // success state.
  return { resolving, resolutionError, resolutionSuccess, setResolutionSuccess, handleResolve };
}
